import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { SnEvent } from '@sportninja/common/features/event/types'
import {
  listMembershipEvents,
  type ListMembershipEventsQuery,
} from '@sportninja/common/services/event/listMembershipEvents'

/**
 * The games query, which is a superset of the events one: it also carries
 * `membership_type` and `per_page`, neither of which `/events/membership`
 * takes. Typed as the superset because that is what the context hands over —
 * the hook narrows it before sending.
 */
export type MyScheduleQuery = ListMembershipEventsQuery & {
  membership_type?: string
}

export interface UseMyScheduleEventsArgs {
  /**
   * The SAME filter object the games request sends, date window included.
   * Games-only keys are stripped inside; everything else passes through, so
   * the two lists can never be bounded differently — that divergence is
   * exactly what this hook exists to prevent.
   */
  query: MyScheduleQuery
  /** False while the filters are still resolving; keeps the query idle. */
  enabled?: boolean
}

export interface UseMyScheduleEventsReturn {
  events: SnEvent[]
  isLoading: boolean
  isError: boolean
  /** Refetches when the list has gone stale; a no-op inside the stale window. */
  refetch: () => void
}

/**
 * Events change often enough that a long window shows stale rows after a
 * teammate adds one. Matches `useScheduleEvents`.
 */
const EVENT_LIST_STALE_TIME = 1000 * 15

/** See the note where this is applied — sized to fetch a schedule in one go. */
const EVENTS_PER_PAGE = 200

const EMPTY: SnEvent[] = []

/**
 * Events for the My Schedule list, from `/events/membership`.
 *
 * The event-side counterpart of `/games/follow`: one request answers for every
 * team the user follows, taking the same filters the games call takes.
 *
 * This REPLACED a per-team approach that could not scale to this screen. That
 * one asked `/teams/{id}/rosters/{rosterId}/events`, which needs a roster id —
 * so it had to fetch `/teams/{id}/schedules/V2` first just to resolve one, and
 * could only ever answer for a single selected team. With All Teams selected
 * there was no team to scope to and it returned nothing at all. The membership
 * endpoint removes the roster lookup, the scope branching, and that blind spot.
 */
export const useMyScheduleEvents = ({
  query,
  enabled = true,
}: UseMyScheduleEventsArgs): UseMyScheduleEventsReturn => {
  'use memo'

  const queryClient = useQueryClient()

  // `membership_type` is games-only: `/games/follow` takes it, this endpoint
  // does not. Stripped HERE rather than at the call site so the context keeps
  // exposing one shared object — the moment the two callers build their own
  // query, the filters can drift apart again, which is what this hook exists
  // to prevent. Also stripped from the key, so toggling a filter the events
  // request ignores cannot invalidate its cache for nothing.
  const { membership_type: _membershipType, ...rest } = query

  // `per_page` comes from the games query, where 45 sizes an infinite-scroll
  // page. Events are fetched COMPLETE (the service follows every page), so a
  // larger page just means fewer round-trips for the same result — 200 puts
  // any realistic schedule in one request. Not unbounded: the service still
  // paginates past it.
  const eventsQuery = { ...rest, per_page: EVENTS_PER_PAGE }
  const queryKey = queryKeys.events.byMembership(eventsQuery)

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => listMembershipEvents(eventsQuery),
    enabled,
    staleTime: EVENT_LIST_STALE_TIME,
    // Overrides the app-wide `refetchOnWindowFocus: false`: an event edited on
    // another device otherwise stays stale here indefinitely.
    refetchOnWindowFocus: true,
  })

  // Not `refetch()` and not `invalidateQueries` — both bypass staleTime and hit
  // the network every call. This refetches only once the window has lapsed.
  const refetch = () => {
    queryClient.refetchQueries({ queryKey, type: 'active', stale: true })
  }

  return {
    events: data ?? EMPTY,
    // A disabled query reports `isLoading: false`; the caller is still waiting
    // on filters, so report loading rather than letting the list settle empty.
    isLoading: enabled ? isLoading : true,
    isError,
    refetch,
  }
}
